// btc15/WindowChart.js — one 15-minute window: Bitcoin against the target,
// and every agent's entry and exit where it happened.
//
// Drawn in the shape the user asked for (2026-09-16): a dashed TARGET rule
// across the whole width with the word on it, a soft gradient under the
// line, the price tinted by which side of the target it is on, and the
// minute marks kept faint so the price is the only loud thing. Points come
// from the desk's recorded path (one a tick) plus, for the live window, the
// browser's own Coinbase prints between ticks.
import { useMemo } from "react";

const W = 600, PAD_L = 8, PAD_R = 8, PAD_T = 20, PAD_B = 20;

export default function WindowChart({
  path = [], livePoints = [], target, wagers = [], nameOf = (k) => k, colors,
  height = 190, testId = "btc15-chart", openTime, showMarket = true, showMinutes = true,
}) {
  const c = { text: "#e5e7eb", muted: "#9ca3af", faint: "#6b7280", border: "#374151",
    green: "#4ade80", red: "#f87171", accent: "#60a5fa", ...colors };
  const H = height;
  const X = (m) => PAD_L + (Math.max(0, Math.min(15, m)) / 15) * (W - PAD_L - PAD_R);
  const minuteOf = (iso) => (openTime ? (new Date(iso).getTime() - new Date(openTime).getTime()) / 60000 : null);

  const pts = useMemo(() => {
    const all = [];
    for (const p of path || []) if (p?.btc > 0) all.push({ m: p.m ?? minuteOf(p.t), btc: Number(p.btc), marketP: p.marketP });
    for (const p of livePoints || []) if (p?.btc > 0) all.push({ m: p.m, btc: Number(p.btc), marketP: null });
    return all.filter((p) => p.m != null && p.m >= 0 && p.m <= 15).sort((a, b) => a.m - b.m);
  }, [path, livePoints]); // eslint-disable-line react-hooks/exhaustive-deps

  const prices = pts.map((p) => p.btc).concat(target > 0 ? [Number(target)] : []);
  for (const w of wagers || []) { if (w.btc > 0) prices.push(Number(w.btc)); if (w.btcAtExit > 0) prices.push(Number(w.btcAtExit)); }
  if (!prices.length) {
    return <div data-testid={testId} style={{ height, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: c.faint }}>No path recorded for this window.</div>;
  }
  const lo0 = Math.min(...prices), hi0 = Math.max(...prices);
  const span = Math.max(hi0 - lo0, Math.max(1, (target || lo0) * 0.0006));
  const lo = lo0 - span * 0.18, hi = hi0 + span * 0.18;
  const Y = (v) => PAD_T + (1 - (v - lo) / (hi - lo)) * (H - PAD_T - PAD_B);
  const YP = (p) => PAD_T + (1 - p) * (H - PAD_T - PAD_B);

  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${X(p.m).toFixed(1)},${Y(p.btc).toFixed(1)}`).join(" ");
  const mktPts = pts.filter((p) => p.marketP != null);
  const mkt = mktPts.map((p, i) => `${i === 0 ? "M" : "L"}${X(p.m).toFixed(1)},${YP(p.marketP).toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const above = last && target ? last.btc >= target : null;
  const lineColor = above == null ? c.accent : above ? c.green : c.red;
  const gid = `btc15grad-${testId}`;
  const ty = target > 0 ? Y(target) : null;

  return (
    <svg data-testid={testId} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
      style={{ display: "block", width: "100%", maxWidth: "100%", height, overflow: "visible" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity={0.28} />
          <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
        </linearGradient>
      </defs>

      {/* the target, all the way across, with the word sitting on it */}
      {ty != null && (
        <g>
          <line x1={PAD_L} x2={W * 0.36} y1={ty} y2={ty} style={{ stroke: c.muted, strokeWidth: 1, strokeDasharray: "5 5", strokeOpacity: 0.75 }} />
          <line x1={W * 0.64} x2={W - PAD_R} y1={ty} y2={ty} style={{ stroke: c.muted, strokeWidth: 1, strokeDasharray: "5 5", strokeOpacity: 0.75 }} />
          <text x={W / 2} y={ty + 4} textAnchor="middle" style={{ fill: c.muted, fontSize: 11, fontWeight: 800, letterSpacing: 1.2 }}>TARGET</text>
        </g>
      )}

      {/* the market's P(Up), faint, underneath everything */}
      {showMarket && mkt && <path d={mkt} style={{ fill: "none", stroke: c.accent, strokeWidth: 1.2, strokeOpacity: 0.35, strokeDasharray: "3 4" }} />}

      {/* Bitcoin */}
      {line && pts.length > 1 && (
        <path d={`${line} L${X(last.m).toFixed(1)},${H - PAD_B} L${X(pts[0].m).toFixed(1)},${H - PAD_B} Z`} style={{ fill: `url(#${gid})` }} />
      )}
      {line && <path d={line} style={{ fill: "none", stroke: lineColor, strokeWidth: 2.4, strokeLinejoin: "round", strokeLinecap: "round" }} />}
      {last && <circle cx={X(last.m)} cy={Y(last.btc)} r={4} style={{ fill: lineColor, stroke: c.border, strokeWidth: 1 }} />}

      {/* minute marks, faint — the price is the loud thing */}
      {showMinutes && [0, 5, 10, 13, 15].map((m) => (
        <text key={m} x={X(m)} y={H - 5} textAnchor={m === 15 ? "end" : m === 0 ? "start" : "middle"}
          style={{ fill: c.faint, fontSize: 9.5, fontFamily: "ui-monospace, Menlo, monospace", opacity: 0.8 }}>
          {m === 15 ? "bell" : `m${m}`}
        </text>
      ))}

      {/* entries and exits */}
      {(wagers || []).map((w) => {
        const mIn = w.placedMinute ?? w.checkpoint ?? 0;
        const yIn = w.btc > 0 ? Y(Number(w.btc)) : (ty != null ? ty : H / 2);
        const sideColor = w.side === "yes" ? c.green : c.red;
        const sold = w.status === "closed" && w.exitMinute != null;
        const settled = w.status === "won" || w.status === "lost";
        const mOut = sold ? w.exitMinute : settled ? 15 : null;
        const yOut = sold && w.btcAtExit > 0 ? Y(Number(w.btcAtExit)) : yIn;
        const won = w.won ?? (w.status === "won");
        const initials = (nameOf(w.agentKey) || w.agentKey || "?").slice(0, 2).toUpperCase();
        return (
          <g key={w.lockId || w._id} data-testid="btc15-marker">
            {mOut != null && <line x1={X(mIn)} x2={X(mOut)} y1={yIn} y2={yOut} style={{ stroke: sideColor, strokeOpacity: 0.45, strokeWidth: 1.5, strokeDasharray: settled ? "2 3" : "0" }} />}
            <g transform={`translate(${X(mIn)},${yIn})`}>
              <path d={w.side === "yes" ? "M0,-7 L6,4 L-6,4 Z" : "M0,7 L6,-4 L-6,-4 Z"} style={{ fill: sideColor, stroke: "#0006", strokeWidth: 0.8 }} />
              <text y={w.side === "yes" ? -10 : 16} textAnchor="middle" style={{ fill: c.text, fontSize: 9, fontWeight: 800, fontFamily: "ui-monospace, Menlo, monospace" }}>{initials}</text>
            </g>
            {sold && (
              <g transform={`translate(${X(mOut)},${yOut})`}>
                <circle r={5} style={{ fill: won ? c.green : c.red, stroke: "#0006", strokeWidth: 0.8 }} />
                <text y={3.2} textAnchor="middle" style={{ fill: "#08131b", fontSize: 8, fontWeight: 900 }}>{won ? "+" : "−"}</text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}
